import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";
import type { Case } from "./useCases";

/**
 * Fetch one case by id. RLS handles authorization server-side; if the
 * caller doesn't have a case role, the query returns null and the UI
 * surfaces "case not found" (deliberately indistinguishable from
 * "you don't have access" to avoid leaking case-existence to outsiders).
 */
export function useCase(caseId: string | undefined) {
  return useQuery<Case | null, Error>({
    queryKey: ["case", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    queryFn: async () => {
      if (!caseId) return null;
      const { data, error } = await berserkr()
        .from("cases")
        .select(
          "id, case_number, court, jurisdiction, caption, filed_at, status, created_at, parsed_metadata",
        )
        .eq("id", caseId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as Case | null) ?? null;
    },
  });
}
